<script>
  import { tick } from "svelte"

  // Renders the body of the server's "add custom field" form, which this island
  // mounts inside. The chosen field type decides which inputs the form even
  // has: choices for a dropdown, an entity type for an entity field, and the
  // required/ask-each-time/default group for everything else.

  // entityTypes are the user's entity types, as [{ id, name }]. When there are
  // none, an entity field cannot be defined and that type is not offered.
  let { entityTypes = [] } = $props()

  let open = $state(false)
  let label = $state("")
  let fieldType = $state("")
  let choices = $state("")
  let entityTypeId = $state("")
  let required = $state(false)
  let requiresInput = $state(true)
  let defaultValue = $state("")

  let labelInput = $state()

  // "Cost Per Item" becomes "cost_per_item".
  let name = $derived(
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, ""),
  )

  async function show() {
    open = true
    await tick()
    labelInput.focus()
  }

  function hide() {
    open = false
    label = ""
    fieldType = ""
    choices = ""
    entityTypeId = ""
    required = false
    requiresInput = true
    defaultValue = ""
  }
</script>

{#if open}
  <div class="box">
    <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
      <h4 class="title is-6 mb-0">Add Custom Field</h4>
      <button type="button" class="delete" aria-label="Cancel" onclick={hide}></button>
    </div>

    <div class="field">
      <label class="label" for="custom_field_label">Field Label</label>
      <div class="control">
        <input
          id="custom_field_label"
          class="input"
          type="text"
          name="custom_field[label]"
          placeholder="e.g., Cost, Mileage, Notes"
          required
          bind:value={label}
          bind:this={labelInput}
        />
      </div>
    </div>

    <input type="hidden" name="custom_field[name]" value={name} />

    <div class="field">
      <span class="label">Field Type</span>
      <div class="type-grid">
        <label class="type-option">
          <input type="radio" name="custom_field[field_type]" value="text" bind:group={fieldType} />
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="4 7 4 4 20 4 20 7"></polyline>
            <line x1="9" y1="20" x2="15" y2="20"></line>
            <line x1="12" y1="4" x2="12" y2="20"></line>
          </svg>
          <span>Text</span>
        </label>

        <label class="type-option">
          <input type="radio" name="custom_field[field_type]" value="number" bind:group={fieldType} />
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="4" y1="9" x2="20" y2="9"></line>
            <line x1="4" y1="15" x2="20" y2="15"></line>
            <line x1="10" y1="3" x2="8" y2="21"></line>
            <line x1="16" y1="3" x2="14" y2="21"></line>
          </svg>
          <span>Number</span>
        </label>

        <label class="type-option">
          <input type="radio" name="custom_field[field_type]" value="currency" bind:group={fieldType} />
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="1" x2="12" y2="23"></line>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
          <span>Currency</span>
        </label>

        <label class="type-option">
          <input type="radio" name="custom_field[field_type]" value="date" bind:group={fieldType} />
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <span>Date</span>
        </label>

        <label class="type-option">
          <input type="radio" name="custom_field[field_type]" value="boolean" bind:group={fieldType} />
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 11 12 14 22 4"></polyline>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
          <span>Yes/No</span>
        </label>

        <label class="type-option">
          <input type="radio" name="custom_field[field_type]" value="select" bind:group={fieldType} />
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6"></path>
          </svg>
          <span>Dropdown</span>
        </label>

        {#if entityTypes.length > 0}
          <label class="type-option">
            <input type="radio" name="custom_field[field_type]" value="entity" bind:group={fieldType} />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span>Entity</span>
          </label>
        {/if}
      </div>
    </div>

    {#if fieldType === "select"}
      <div class="field">
        <label class="label" for="select_choices">Choices (one per line)</label>
        <div class="control">
          <textarea
            id="select_choices"
            name="select_choices"
            class="textarea"
            rows="3"
            placeholder={"Option 1\nOption 2\nOption 3"}
            bind:value={choices}
          ></textarea>
        </div>
      </div>
    {/if}

    {#if fieldType === "entity"}
      <div class="field">
        <label class="label" for="association_entity_type_id">Entity Type</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select id="association_entity_type_id" name="association_entity_type_id" bind:value={entityTypeId}>
              <option value="">Any entity type</option>
              {#each entityTypes as entityType (entityType.id)}
                <option value={entityType.id}>{entityType.name}</option>
              {/each}
            </select>
          </div>
        </div>
      </div>
    {:else}
      <div class="field">
        <label class="checkbox">
          <input type="hidden" name="custom_field[required]" value="0" />
          <input type="checkbox" name="custom_field[required]" value="1" bind:checked={required} />
          Required
        </label>
        <p class="help">Must have a value to save</p>
      </div>

      <div class="field">
        <label class="checkbox">
          <input type="hidden" name="custom_field[requires_input]" value="0" />
          <input type="checkbox" name="custom_field[requires_input]" value="1" bind:checked={requiresInput} />
          Ask each time
        </label>
        <p class="help">Prompt for value on each occurrence</p>
      </div>

      <div class="field">
        <label class="label" for="custom_field_default_value">Default value</label>
        <div class="control">
          <input
            id="custom_field_default_value"
            class="input"
            type="text"
            name="custom_field[default_value]"
            placeholder="Used when 'Ask each time' is off"
            bind:value={defaultValue}
          />
        </div>
      </div>
    {/if}

    <div class="field">
      <div class="control">
        <input type="submit" class="button is-primary" value="Add Field" />
      </div>
    </div>
  </div>
{:else}
  <button type="button" class="button" onclick={show}>
    <span class="icon">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
    </span>
    <span>Add custom field</span>
  </button>
{/if}

<style>
  .type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
    gap: 0.5rem;
  }

  /* The radio itself is the state; the tile around it is what is seen, so the
     input is taken out of the layout rather than hidden (which would take it
     out of the tab order too). */
  .type-option {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.75rem 0.5rem;
    border: 1px solid var(--bulma-border);
    border-radius: var(--bulma-radius);
    font-size: 0.8rem;
    cursor: pointer;
  }

  .type-option input {
    position: absolute;
    opacity: 0;
  }

  .type-option:has(input:checked) {
    border-color: var(--bulma-link);
    background-color: var(--bulma-scheme-main-bis);
  }

  .type-option:has(input:focus-visible) {
    outline: 2px solid var(--bulma-link);
    outline-offset: 2px;
  }
</style>
